import type { HREmployee, HRLeaveBalance, HRLeaveRequest } from '@cloud/db'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Check, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { LeaveTypeBadge } from '@/components/hr/leave-type-badge'
import { StatusBadge } from '@/components/hr/status-badge'
import { ModuleGuard } from '@/components/module-guard'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

const leaveRequestSchema = z.object({
  employeeId: z.number().min(1, 'Employee is required'),
  leaveType: z.enum(['annual', 'sick', 'maternity', 'paternity', 'unpaid', 'bereavement']),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  totalDays: z.number().min(1, 'Total days is required'),
  reason: z.string().optional(),
})

type LeaveRequestFormValues = z.infer<typeof leaveRequestSchema>

export const Route = createFileRoute('/hr/leave')({
  component: LeavePage,
})

function LeavePage() {
  return (
    <ModuleGuard moduleKey="hr">
      <LeaveManagement />
    </ModuleGuard>
  )
}

function LeaveManagement() {
  const [activeTab, setActiveTab] = useState<'requests' | 'balances'>('requests')
  // 'all' is the sentinel for "no filter" — Radix Select doesn't allow
  // empty-string values, so we use a literal 'all' here and coerce to
  // undefined when building the query.
  const [employeeFilter, setEmployeeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [leaveTypeFilter, setLeaveTypeFilter] = useState<string>('all')
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; requestId: number | null }>({
    open: false,
    requestId: null,
  })
  const [rejectReason, setRejectReason] = useState('')

  const {
    data: requestsData,
    isLoading,
    refetch,
  } = useQuery(
    orpc.hr.getLeaveRequests.queryOptions({
      input: {
        employeeId:
          employeeFilter && employeeFilter !== 'all' ? Number(employeeFilter) : undefined,
        status:
          statusFilter === 'all'
            ? undefined
            : (statusFilter as 'pending' | 'approved' | 'rejected' | 'cancelled'),
        leaveType:
          leaveTypeFilter === 'all'
            ? undefined
            : (leaveTypeFilter as
                | 'annual'
                | 'sick'
                | 'maternity'
                | 'paternity'
                | 'unpaid'
                | 'bereavement'),
      },
    }),
  )

  const { data: employeesData } = useQuery(
    orpc.hr.getEmployees.queryOptions({ input: { page: 1, limit: 100 } }),
  )

  const requests = (requestsData ?? []) as HRLeaveRequest[]
  const employees = (employeesData?.data ?? []) as HREmployee[]

  const approveMutation = useMutation(
    orpc.hr.approveLeaveRequest.mutationOptions({
      onSuccess: () => {
        toast.success('Leave request approved')
        refetch()
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const rejectMutation = useMutation(
    orpc.hr.rejectLeaveRequest.mutationOptions({
      onSuccess: () => {
        toast.success('Leave request rejected')
        refetch()
        setRejectDialog({ open: false, requestId: null })
        setRejectReason('')
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const createMutation = useMutation(
    orpc.hr.createLeaveRequest.mutationOptions({
      onSuccess: () => {
        toast.success('Leave request created')
        refetch()
        setIsFormOpen(false)
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const initializeBalancesMutation = useMutation(
    orpc.hr.initializeLeaveBalances.mutationOptions({
      onSuccess: (data: unknown) => {
        const created = (data as { created?: number } | null)?.created ?? 0
        toast.success(`Initialized leave balances for ${created} employees`)
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const getEmployeeName = (id: number) => {
    const emp = employees.find((e) => e.id === id)
    return emp ? `${emp.firstName} ${emp.lastName}` : `Employee #${id}`
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Leave Management</h1>
          <p className="text-muted-foreground">Manage leave requests and balances</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Submit Leave Request
        </Button>
      </div>

      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'requests' ? 'default' : 'outline'}
          onClick={() => setActiveTab('requests')}
        >
          Requests
        </Button>
        <Button
          variant={activeTab === 'balances' ? 'default' : 'outline'}
          onClick={() => setActiveTab('balances')}
        >
          Balances
        </Button>
      </div>

      {activeTab === 'requests' && (
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-4">
              <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id.toString()}>
                      {emp.firstName} {emp.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={leaveTypeFilter} onValueChange={setLeaveTypeFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                  <SelectItem value="sick">Sick</SelectItem>
                  <SelectItem value="maternity">Maternity</SelectItem>
                  <SelectItem value="paternity">Paternity</SelectItem>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="bereavement">Bereavement</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No leave requests found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Leave Type</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>{getEmployeeName(request.employeeId)}</TableCell>
                      <TableCell>
                        <LeaveTypeBadge leaveType={request.leaveType} />
                      </TableCell>
                      <TableCell>{new Date(request.startDate).toLocaleDateString()}</TableCell>
                      <TableCell>{new Date(request.endDate).toLocaleDateString()}</TableCell>
                      <TableCell>{request.totalDays}</TableCell>
                      <TableCell>
                        <StatusBadge status={request.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        {request.status === 'pending' && (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => approveMutation.mutate({ id: request.id })}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600"
                              onClick={() => setRejectDialog({ open: true, requestId: request.id })}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'balances' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => {
                const year = new Date().getFullYear()
                initializeBalancesMutation.mutate({ year })
              }}
              disabled={initializeBalancesMutation.isPending}
            >
              {initializeBalancesMutation.isPending
                ? 'Initializing...'
                : 'Initialize Leave Balances'}
            </Button>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No employees found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {employees.map((employee) => (
                <LeaveBalanceCard key={employee.id} employee={employee} />
              ))}
            </div>
          )}
        </div>
      )}

      <CreateLeaveRequestDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        employees={employees}
        onSubmit={async (data) => {
          await createMutation.mutateAsync(data)
        }}
      />

      <AlertDialog
        open={rejectDialog.open}
        onOpenChange={(open) => setRejectDialog({ open, requestId: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Leave Request</AlertDialogTitle>
            <AlertDialogDescription>
              Please provide a reason for rejecting this leave request.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Rejection reason..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="mt-4"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (rejectDialog.requestId && rejectReason) {
                  rejectMutation.mutate({
                    id: rejectDialog.requestId,
                    reason: rejectReason,
                  })
                }
              }}
              disabled={!rejectReason || rejectMutation.isPending}
            >
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function LeaveBalanceCard({ employee }: { employee: HREmployee }) {
  const { data: balances } = useQuery(
    orpc.hr.getLeaveBalance.queryOptions({ input: { employeeId: employee.id } }),
  )
  const leaveBalances = (balances ?? []) as HRLeaveBalance[]

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          {employee.firstName} {employee.lastName}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {leaveBalances.length === 0 ? (
          <p className="text-sm text-muted-foreground">No leave balances</p>
        ) : (
          leaveBalances.map((balance) => (
            <div key={balance.id}>
              <div className="flex justify-between text-sm mb-1">
                <span className="capitalize">{balance.leaveType}</span>
                <span className="text-muted-foreground">
                  {balance.remaining}/{balance.totalAllowed}
                </span>
              </div>
              <Progress value={(balance.used / balance.totalAllowed) * 100} className="h-2" />
            </div>
          ))
        )}
      </CardContent>
    </Card>
  )
}

interface CreateLeaveRequestDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employees: HREmployee[]
  onSubmit: (data: LeaveRequestFormValues) => Promise<void>
}

function CreateLeaveRequestDialog({
  open,
  onOpenChange,
  employees,
  onSubmit,
}: CreateLeaveRequestDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<LeaveRequestFormValues>({
    resolver: zodResolver(leaveRequestSchema),
    defaultValues: {
      leaveType: 'annual',
      totalDays: 1,
    },
  })

  const startDate = form.watch('startDate')
  const endDate = form.watch('endDate')

  const calculateDays = () => {
    if (startDate && endDate) {
      const start = new Date(startDate)
      const end = new Date(endDate)
      const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
      form.setValue('totalDays', diff > 0 ? diff : 1)
    }
  }

  const handleSubmit = async (data: LeaveRequestFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Submit Leave Request</DialogTitle>
          <DialogDescription>Request time off from work</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="employeeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employee</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={field.value?.toString() ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id.toString()}>
                          {emp.firstName} {emp.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="leaveType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Leave Type</FormLabel>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(
                        value as
                          | 'annual'
                          | 'sick'
                          | 'maternity'
                          | 'paternity'
                          | 'unpaid'
                          | 'bereavement',
                      )
                    }
                    value={field.value ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select leave type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="annual">Annual Leave</SelectItem>
                      <SelectItem value="sick">Sick Leave</SelectItem>
                      <SelectItem value="maternity">Maternity Leave</SelectItem>
                      <SelectItem value="paternity">Paternity Leave</SelectItem>
                      <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                      <SelectItem value="bereavement">Bereavement Leave</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e)
                          setTimeout(calculateDays, 0)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e)
                          setTimeout(calculateDays, 0)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="totalDays"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Total Days</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Reason for leave..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
